import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NotificationStatus } from '@swi/contracts';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import * as api from '../api/endpoints';
import { isApiError } from '../api/problem';
import { PageHeader } from '../components/layout/PageHeader';
import {
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Kpi,
  Pill,
  Select,
  Skeleton,
} from '../components/ui/primitives';
import { dateTime, number, relative } from '../lib/format';

/**
 * Health of the external Notification Service.
 *
 * A failed notification never invalidates a confirmed reservation — it lands
 * here as a FAILED row that the operator can retry once the service is back.
 */
export function NotificationHealthPage() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<NotificationStatus | ''>('');

  const health = useQuery({
    queryKey: ['notifications', 'health'],
    queryFn: api.notificationHealth,
    refetchInterval: 20_000,
  });

  const logs = useQuery({
    queryKey: ['notifications', 'all', status],
    queryFn: () =>
      api.listNotifications({ status: status === '' ? undefined : status, pageSize: 50 }),
  });

  const retry = useMutation({
    mutationFn: (id: number) => api.retryNotification(id),
    onSuccess: (log) => {
      toast[log.status === 'SENT' ? 'success' : 'warning'](
        log.status === 'SENT' ? 'Notifikace odeslána' : 'Odeslání opět selhalo',
        { description: log.lastError ?? undefined },
      );
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (error: unknown) => {
      toast.error(isApiError(error) ? error.humanMessage : 'Retry se nepovedl');
    },
  });

  return (
    <>
      <PageHeader
        title="Notification health"
        description="Externí Notification Service je hranice systému. Její výpadek se tu zaznamená, ale nikdy nezneplatní potvrzenou rezervaci."
      />

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
        {health.isLoading ? (
          Array.from({ length: 6 }, (_, index) => <Skeleton key={index} className="h-24" />)
        ) : health.data ? (
          <>
            <Kpi label="Celkem zpráv" value={health.data.total} />
            <Kpi label="Odesláno" value={health.data.sent} tone="accent" />
            <Kpi
              label="Selhalo"
              value={health.data.failed}
              tone={health.data.failed > 0 ? 'danger' : 'neutral'}
            />
            <Kpi label="K opakování" value={health.data.retry} tone={health.data.retry > 0 ? 'warn' : 'neutral'} />
            <Kpi
              label="Failure rate"
              value={number(health.data.failureRatePct, 1)}
              unit="%"
              tone={health.data.failureRatePct > 5 ? 'danger' : 'neutral'}
            />
            <Kpi
              label="Circuit breaker"
              value={health.data.circuitState}
              tone={health.data.circuitState === 'CLOSED' ? 'accent' : 'danger'}
              hint={
                health.data.lastErrorAt === null
                  ? 'žádná chyba'
                  : `poslední chyba ${relative(health.data.lastErrorAt)}`
              }
            />
          </>
        ) : null}
      </div>

      <Card>
        <CardHeader
          title="Log volání"
          subtitle="Každý pokus o odeslání, včetně počtu opakování a chyby"
          action={
            <Field label="Stav">
              <Select
                value={status}
                onChange={(event) => setStatus(event.target.value as NotificationStatus | '')}
              >
                <option value="">Vše</option>
                <option value="SENT">SENT</option>
                <option value="FAILED">FAILED</option>
                <option value="RETRY">RETRY</option>
              </Select>
            </Field>
          }
        />

        {logs.isLoading ? (
          <Skeleton className="h-48" />
        ) : (logs.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="Žádné záznamy" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-[13px]">
              <thead>
                <tr className="text-left">
                  {['Kdy', 'Šablona', 'Kanál', 'Rezervace', 'Pokusů', 'Stav', 'Chyba', ''].map(
                    (head) => (
                      <th key={head} className="label-caps pb-2">
                        {head}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {logs.data?.items.map((log) => (
                  <tr key={log.id} className="border-t border-[var(--border-subtle)] align-top">
                    <td className="mono py-2 text-[11px] whitespace-nowrap text-[var(--text-secondary)]">
                      {dateTime(log.createdAt)}
                    </td>
                    <td className="mono py-2">{log.template}</td>
                    <td className="py-2">
                      <Pill tone="info">{log.channel}</Pill>
                    </td>
                    <td className="mono py-2 text-[11px]">
                      {log.reservationId === null ? (
                        '—'
                      ) : (
                        <Link
                          to={`/reservations/${log.reservationId}`}
                          className="text-[var(--accent)] hover:underline"
                        >
                          {log.reservationId.slice(0, 8)}…
                        </Link>
                      )}
                    </td>
                    <td className="mono py-2">{log.attempts}</td>
                    <td className="py-2">
                      <Pill
                        tone={
                          log.status === 'SENT' ? 'accent' : log.status === 'FAILED' ? 'danger' : 'warn'
                        }
                      >
                        {log.status}
                      </Pill>
                    </td>
                    <td className="mono max-w-56 py-2 text-[11px] text-[var(--danger)]">
                      {log.lastError ?? ''}
                    </td>
                    <td className="py-2">
                      {log.status !== 'SENT' && (
                        <Button
                          size="sm"
                          variant="outline"
                          loading={retry.isPending && retry.variables === log.id}
                          onClick={() => retry.mutate(log.id)}
                        >
                          Retry
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
